import { Paths } from '@chronosphereio/shared';
import { get, merge, set } from 'lodash';
import { JSONValue } from '@/core';
import { BasePanelProps } from '../model';
import { CommonChartProps } from './plugins';

/**
 * Given a propToSpecMapping and a spec returns the corresponding props for the chart.
 * If no mapping is provided, it is assumed that the spec maps one to one to the props.
 */
export const mapSpecToProps = <ComponentProps extends BasePanelProps, DashboardSpec extends JSONValue>(
  spec: DashboardSpec,
  propToSpecMapping: Partial<Record<keyof ComponentProps, Paths<DashboardSpec>>>
): Omit<ComponentProps, CommonChartProps> => {
  let props = {};
  for (const [chartProp, chartSpec] of Object.entries(propToSpecMapping)) {
    const propValue = get(spec, chartSpec);
    if (propValue !== undefined) {
      props = merge(props, { [chartProp]: propValue });
    }
  }

  return props as Omit<ComponentProps, CommonChartProps>;
};

/**
 * Given a propToSpecMapping and props returns the corresponding spec for the chart.
 */
export const mapPropsToSpec = <ComponentProps extends BasePanelProps, DashboardSpec extends JSONValue>(
  props: Omit<ComponentProps, CommonChartProps>,
  propToSpecMapping: Partial<Record<keyof ComponentProps, Paths<DashboardSpec>>>
) => {
  const spec: Partial<DashboardSpec> = {};
  for (const [chartProp, chartSpec] of Object.entries(propToSpecMapping)) {
    const value = props[chartProp as keyof Omit<ComponentProps, CommonChartProps>];
    if (value !== undefined) {
      set(spec, chartSpec, value);
    }
  }

  return spec as DashboardSpec;
};
